import type {KeyTextField} from '@prismicio/client'
import type {FC} from 'react'
import type {Heading} from '../utils'

type TableOfContentsProps = {
  headings: Heading[]
  title?: KeyTextField | string
}

/**
 * Table of contents navigation - responsive component that adapts styling for mobile/desktop
 * Desktop: Simple list with dividers
 * Mobile: Card with rounded borders and full-width separators
 */
export const TableOfContents: FC<TableOfContentsProps> = ({headings, title}) => {
  if (headings.length === 0) {
    return null
  }

  return (
    <div className="flex flex-col gap-3 min-[1100px]:gap-3">
      {title && (
        <span className="text-detail text-moss-grey-400 max-[1100px]:tracking-[0.6px] min-[1100px]:tracking-normal">
          {title}
        </span>
      )}

      {/* Desktop: Simple list */}
      <ul className="hidden list-none flex-col gap-3 divide-y-[1px] divide-moss-grey-150 min-[1100px]:flex">
        {headings.map((heading) => (
          <li className="group pt-3" key={heading.id}>
            <a
              href={`#${heading.id}`}
              className="text-tg-body-m text-moss-grey-500 transition-colors duration-300 hover:text-moss-black"
            >
              {heading.text}
              <span className="pl-1 text-green-default transition-all group-hover:pl-2">→</span>
            </a>
          </li>
        ))}
      </ul>

      {/* Mobile: Spaced items with separators */}
      <div className="flex flex-col gap-3 min-[1100px]:hidden">
        {headings.map((heading, index) => (
          <div key={heading.id} className="flex flex-col gap-3">
            <a
              href={`#${heading.id}`}
              className="text-tg-body-m flex items-center justify-between gap-1 text-moss-grey-500"
            >
              <span>{heading.text}</span>
              <span className="text-green-default">→</span>
            </a>
            {index < headings.length - 1 && <div className="h-px w-full bg-moss-grey-150" />}
          </div>
        ))}
      </div>
    </div>
  )
}
